import React from "react"
import { ArrowUpRight, TrendingUp } from "lucide-react"
import { trackEvent } from "../utils/tracking"

const formatPrice = (rawPrice) => {
  const parsed = Number.parseFloat(rawPrice)
  return Number.isFinite(parsed) ? parsed.toFixed(2) : "0.00"
}

const formatRtpUp = (rawRtp) => {
  const parsed = Number.parseFloat(rawRtp)
  if (!Number.isFinite(parsed)) return "0.0"
  return (Math.ceil(parsed * 10) / 10).toFixed(1)
}

const CaseDetails = ({ image, title, website, price, rtp, game, subpageLinks }) => {
  const formattedPrice = formatPrice(price)
  const formattedRtp = formatRtpUp(rtp)
  const formattedGame = game ? String(game).toUpperCase() : ""

  return (
    <article className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-[#2A2D31]/60 bg-gradient-to-b from-[#171A1E]/95 to-[#0D0F11]/95 shadow-[0_10px_30px_rgba(0,0,0,0.16)] backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-[#FF6B00]/40 hover:shadow-[0_16px_40px_rgba(255,107,0,0.14)]">
      <div className="absolute inset-x-8 top-0 z-20 h-px bg-gradient-to-r from-transparent via-[#FF9A3D]/70 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

      <div className="relative h-48 overflow-hidden border-b border-[#2A2D31]/40 bg-[radial-gradient(circle_at_50%_45%,rgba(255,107,0,0.10),transparent_62%)]">
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-black/20" />
        <img
          src={image || "/placeholder.svg"}
          alt={title || "Case"}
          className="relative z-10 h-full w-full object-contain p-7 transition-transform duration-500 ease-out group-hover:scale-110"
        />

        <div className="absolute right-3 top-3 z-20 inline-flex items-center gap-1.5 rounded-full border border-[#FF6B00]/30 bg-[#0A0C10]/85 px-2.5 py-1 text-xs font-semibold text-[#FF9A3D] shadow-[0_4px_16px_rgba(0,0,0,0.35)] backdrop-blur-md">
          <TrendingUp className="h-3.5 w-3.5" />
          {formattedRtp}% RTP
        </div>
      </div>

      <div className="flex flex-1 flex-col p-5 text-white">
        {website && (
          <p className="mb-2 truncate text-center text-xs font-semibold uppercase tracking-[0.14em] text-[#FF9A3D]/80">
            {website}
          </p>
        )}

        <h3 className="line-clamp-2 min-h-[3.25rem] text-center font-display text-lg font-semibold leading-snug text-white transition-colors duration-300 group-hover:text-[#FFB26F]">
          {title}
        </h3>

        <div className={`mt-4 grid gap-2.5 ${formattedGame ? "grid-cols-2" : "grid-cols-1"}`}>
          <div className="rounded-xl border border-[#2A2D31]/50 bg-[#0A0C10]/55 px-3 py-2.5">
            <span className="block text-[11px] uppercase tracking-wider text-blue-100/45">Site price</span>
            <span className="mt-0.5 block font-display text-base font-semibold text-white">
              {formattedPrice}
            </span>
          </div>
          {formattedGame && (
            <div className="rounded-xl border border-[#2A2D31]/50 bg-[#0A0C10]/55 px-3 py-2.5">
              <span className="block text-[11px] uppercase tracking-wider text-blue-100/45">Game</span>
              <span className="mt-0.5 block font-display text-base font-semibold text-[#FF9A3D]">
                {formattedGame}
              </span>
            </div>
          )}
        </div>

        <a
          href={subpageLinks}
          target="_blank"
          rel="noopener noreferrer"
          data-popup-gate="true"
          aria-disabled={!subpageLinks}
          className={`mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-[#FF8A2A]/20 bg-gradient-to-r from-[#FF6B00] to-[#FF9A3D] py-2.5 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(255,107,0,0.16)] transition-all duration-300 hover:shadow-[0_10px_26px_rgba(255,107,0,0.28)] ${
            !subpageLinks ? "pointer-events-none opacity-50" : ""
          }`}
          onClick={() => {
            try {
              trackEvent("OpenCase", { title, url: subpageLinks })
            } catch (err) {
              // Tracking should never block navigation.
            }
          }}
        >
          Open Case
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
      </div>
    </article>
  )
}

export default CaseDetails
